import type { KioskFlowApi } from './kiosk-flow-api';
import type { KioskFlowPushApi } from './kiosk-flow-push-api';
import type {
  KioskFlow,
  KioskFlowNameRequest,
  KioskFlowOperatorContext,
  KioskFlowPublishResult,
  KioskFlowSaveRequest,
  KioskImageOption,
  KioskLayoutOption,
} from '../types/kiosk-flow-types';
import { KioskFlowConflictError, KioskFlowValidationFailure } from '../types/kiosk-flow-errors';
import { groupLayouts, listImages } from './kiosk-layout-mapper';
import { mergeFlowIntoSettings } from '../utils/kiosk-settings-merge';

function toError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback);
}

// The backoffice serializes ErrorCode as its name; 409 covers a numeric serializer
function isConflict(errorCode: string | number | null | undefined): boolean {
  return errorCode === 'Conflict' || errorCode === 409;
}

export class KioskFlowRepository {
  constructor(
    private readonly api: KioskFlowApi,
    private readonly pushApi: KioskFlowPushApi
  ) {}

  // Resolves to null when the operator has no flow yet
  async getFlow(ctx: KioskFlowOperatorContext): Promise<KioskFlow | null> {
    try {
      const response = await this.api.getFlow(ctx);
      if (!response?.isSuccess) throw new Error(response?.errorMessage || 'Failed to load kiosk flow');
      return response.flow ?? null;
    } catch (error: unknown) {
      throw toError(error, 'Failed to load kiosk flow');
    }
  }

  async createFlow(ctx: KioskFlowOperatorContext, request: KioskFlowNameRequest): Promise<KioskFlow> {
    try {
      const response = await this.api.createFlow(ctx, request);
      if (!response?.isSuccess || !response.flow) {
        throw new Error(response?.errorMessage || 'Failed to create kiosk flow');
      }
      return response.flow;
    } catch (error: unknown) {
      throw toError(error, 'Failed to create kiosk flow');
    }
  }

  async renameFlow(ctx: KioskFlowOperatorContext, request: KioskFlowNameRequest): Promise<KioskFlow> {
    try {
      const response = await this.api.renameFlow(ctx, request);
      if (!response?.isSuccess || !response.flow) {
        throw new Error(response?.errorMessage || 'Failed to rename kiosk flow');
      }
      return response.flow;
    } catch (error: unknown) {
      throw toError(error, 'Failed to rename kiosk flow');
    }
  }

  async deleteFlow(ctx: KioskFlowOperatorContext): Promise<void> {
    try {
      const response = await this.api.deleteFlow(ctx);
      if (!response?.isSuccess) throw new Error(response?.errorMessage || 'Failed to delete kiosk flow');
    } catch (error: unknown) {
      throw toError(error, 'Failed to delete kiosk flow');
    }
  }

  async saveFlow(ctx: KioskFlowOperatorContext, request: KioskFlowSaveRequest): Promise<KioskFlow> {
    try {
      const response = await this.api.saveFlow(ctx, request);
      if (!response?.isSuccess || !response.flow) {
        throw new Error(response?.errorMessage || 'Failed to save kiosk flow');
      }
      return response.flow;
    } catch (error: unknown) {
      throw toError(error, 'Failed to save kiosk flow');
    }
  }

  async getLayouts(ctx: KioskFlowOperatorContext): Promise<KioskLayoutOption[]> {
    try {
      const response = await this.api.getLayouts(ctx);
      if (response?.isSuccess === false) {
        throw new Error(response.errorMessage || 'Failed to load layouts');
      }
      return groupLayouts(response?.items ?? [], ctx.operatorId);
    } catch (error: unknown) {
      throw toError(error, 'Failed to load layouts');
    }
  }

  // The operator's kiosk images (title + file name) — the promo banner choices
  async getImages(ctx: KioskFlowOperatorContext): Promise<KioskImageOption[]> {
    try {
      const response = await this.api.getImages(ctx);
      if (response?.isSuccess === false) {
        throw new Error(response.errorMessage || 'Failed to load images');
      }
      return listImages(response?.items ?? [], ctx.operatorId);
    } catch (error: unknown) {
      throw toError(error, 'Failed to load images');
    }
  }

  // Returns the JSON to push; throws KioskFlowValidationFailure when the draft can't be published
  async preparePublish(ctx: KioskFlowOperatorContext): Promise<string> {
    try {
      const response = await this.api.preparePublish(ctx);
      if (!response?.isSuccess || !response.json) {
        const message = response?.errorMessage || 'Failed to prepare the flow for publishing';
        if (response?.validationErrors?.length) {
          throw new KioskFlowValidationFailure(message, response.validationErrors);
        }
        throw new Error(message);
      }
      return response.json;
    } catch (error: unknown) {
      throw toError(error, 'Failed to prepare the flow for publishing');
    }
  }

  // Throws KioskFlowConflictError when the draft changed since prepare
  async recordVersion(ctx: KioskFlowOperatorContext, jsonSnapshot: string): Promise<KioskFlowPublishResult> {
    try {
      const response = await this.api.recordVersion(ctx, { jsonSnapshot });
      if (!response?.isSuccess || !response.version || !response.flow) {
        const message = response?.errorMessage || 'Failed to record the published version';
        if (isConflict(response?.errorCode)) throw new KioskFlowConflictError(message);
        throw new Error(message);
      }
      return { version: response.version, flow: response.flow };
    } catch (error: unknown) {
      throw toError(error, 'Failed to record the published version');
    }
  }

  // Read-merge-write of the operator's Kiosk API settings with notify=true — every kiosk of the
  // operator restarts on the new flow. Any failure throws, so the caller must not record the version.
  async pushFlow(operatorId: number, flowJson: string): Promise<void> {
    try {
      // Read-merge-write with no version check: addOperator replaces the whole record, so a settings
      // save (e.g. Kiosk Customizer) between the read and the write is lost. Needs Kiosk API support (PS-367)
      const operator = await this.pushApi.getOperator(operatorId);
      if (!operator?.id) throw new Error('Operator is not set up in the Kiosk API');
      const settings = mergeFlowIntoSettings(operator.settings, flowJson);
      await this.pushApi.saveOperatorAndNotify({ ...operator, settings });
    } catch (error: unknown) {
      throw toError(error, 'Failed to push the flow to the kiosks');
    }
  }
}
